import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
  ReactNode,
} from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { useAuth } from './AuthContext';
import { StartupProfile } from '../types/firestore';
import { resolveStartupId } from '../utils/startupId';

const FAVORITES_TTL_MS = 5 * 60 * 1000;

interface FavoritesContextType {
  favorites: StartupProfile[];
  favoritesCount: number | null;
  /** True when a favorite was added since the investor last opened Favorites. */
  hasUnseenFavorites: boolean;
  favoritesLoading: boolean;
  favoritesReady: boolean;
  isFavorited: (startupId: string) => boolean;
  refreshFavorites: () => Promise<void>;
  invalidateFavorites: () => void;
  removeFavoriteLocally: (startupId: string) => void;
  /** Optimistic re-follow so inbox doesn't stay "ended" while the list refetches. */
  addFavoriteLocally: (startupId: string, profile?: Partial<StartupProfile>) => void;
  setFavoritesCount: (count: number | null) => void;
  markFavoritesSeen: () => void;
}

const seenFavoritesKey = (userId: string) => `prairi.favorites.seen:${userId}`;

function favoriteIdsOf(favorites: StartupProfile[]): string[] {
  return favorites
    .map(favorite => resolveStartupId(favorite))
    .filter((id): id is string => Boolean(id));
}

function readSeenFavoriteIds(userId: string): Set<string> {
  try {
    const raw = localStorage.getItem(seenFavoritesKey(userId));
    const parsed = raw ? JSON.parse(raw) : [];
    return new Set(Array.isArray(parsed) ? parsed.filter(id => typeof id === 'string') : []);
  } catch {
    return new Set();
  }
}

const FavoritesContext = createContext<FavoritesContextType | undefined>(undefined);

function normalizeDates(startup: any): StartupProfile {
  return {
    ...startup,
    createdAt: startup.createdAt?.seconds
      ? new Date(startup.createdAt.seconds * 1000)
      : startup.createdAt instanceof Date
        ? startup.createdAt
        : new Date(),
    updatedAt: startup.updatedAt?.seconds
      ? new Date(startup.updatedAt.seconds * 1000)
      : startup.updatedAt instanceof Date
        ? startup.updatedAt
        : new Date(),
    lastUpdatedAt: startup.lastUpdatedAt?.seconds
      ? new Date(startup.lastUpdatedAt.seconds * 1000)
      : startup.lastUpdatedAt instanceof Date
        ? startup.lastUpdatedAt
        : new Date(),
  };
}

export const FavoritesCountProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const functions = getFunctions();
  const [favorites, setFavorites] = useState<StartupProfile[]>([]);
  const [favoritesCount, setFavoritesCountState] = useState<number | null>(null);
  const [seenFavoriteIds, setSeenFavoriteIds] = useState<Set<string>>(new Set());
  const [favoritesLoading, setFavoritesLoading] = useState(false);
  const [fetchedAt, setFetchedAt] = useState<number | null>(null);
  const fetchGenRef = useRef(0);
  /** Ids added optimistically; kept across a refetch that races ahead of the server write. */
  const pendingAddIdsRef = useRef<Set<string>>(new Set());

  const isInvestor = user?.userType === 'investor';
  const favoritesReady = fetchedAt !== null;

  const fetchFavorites = useCallback(async () => {
    if (!isInvestor) return;
    const gen = ++fetchGenRef.current;
    setFavoritesLoading(true);
    try {
      const getFav = httpsCallable(functions, 'getFavorites');
      const result = await getFav({});
      if (gen !== fetchGenRef.current) return;
      const data = result.data as { success?: boolean; favorites?: any[] };
      if (data?.success && Array.isArray(data.favorites)) {
        const normalized = data.favorites.map(normalizeDates);
        const serverIds = new Set(
          normalized.map(item => resolveStartupId(item)).filter((id): id is string => Boolean(id))
        );
        Array.from(pendingAddIdsRef.current).forEach(id => {
          if (serverIds.has(id)) pendingAddIdsRef.current.delete(id);
        });
        setFavorites(prev => {
          const pendingStubs = prev.filter(favorite => {
            const id = resolveStartupId(favorite);
            return Boolean(id && pendingAddIdsRef.current.has(id) && !serverIds.has(id));
          });
          const merged = pendingStubs.length > 0 ? [...pendingStubs, ...normalized] : normalized;
          setFavoritesCountState(merged.length);
          return merged;
        });
      } else {
        setFavorites(prev => {
          const pendingStubs = prev.filter(favorite => {
            const id = resolveStartupId(favorite);
            return Boolean(id && pendingAddIdsRef.current.has(id));
          });
          setFavoritesCountState(pendingStubs.length);
          return pendingStubs;
        });
      }
      setFetchedAt(Date.now());
    } catch {
      if (gen !== fetchGenRef.current) return;
      setFavorites(prev => {
        const pendingStubs = prev.filter(favorite => {
          const id = resolveStartupId(favorite);
          return Boolean(id && pendingAddIdsRef.current.has(id));
        });
        setFavoritesCountState(pendingStubs.length);
        return pendingStubs;
      });
    } finally {
      if (gen === fetchGenRef.current) setFavoritesLoading(false);
    }
  }, [isInvestor, functions]);

  // Auto-fetch on investor login; respect TTL
  useEffect(() => {
    if (!isInvestor || !user?.id) {
      pendingAddIdsRef.current.clear();
      setFavorites([]);
      setFavoritesCountState(null);
      setSeenFavoriteIds(new Set());
      setFetchedAt(null);
      return;
    }
    setSeenFavoriteIds(readSeenFavoriteIds(user.id));
  }, [isInvestor, user?.id]);

  const markFavoritesSeen = useCallback(() => {
    if (!user?.id) return;
    const ids = favoriteIdsOf(favorites);
    localStorage.setItem(seenFavoritesKey(user.id), JSON.stringify(ids));
    setSeenFavoriteIds(new Set(ids));
  }, [favorites, user?.id]);

  // The favorites already on the account are not "new". Red starts only after a later one arrives.
  useEffect(() => {
    if (!isInvestor || !user?.id || !favoritesReady) return;
    const key = seenFavoritesKey(user.id);
    if (localStorage.getItem(key) !== null) return;
    const ids = favoriteIdsOf(favorites);
    localStorage.setItem(key, JSON.stringify(ids));
    setSeenFavoriteIds(new Set(ids));
  }, [isInvestor, user?.id, favoritesReady, favorites]);

  const hasUnseenFavorites = favoriteIdsOf(favorites).some(id => !seenFavoriteIds.has(id));

  useEffect(() => {
    if (!isInvestor) {
      return;
    }
    const isStale = !fetchedAt || Date.now() - fetchedAt > FAVORITES_TTL_MS;
    if (isStale) fetchFavorites();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isInvestor]);

  const refreshFavorites = useCallback(() => fetchFavorites(), [fetchFavorites]);

  const invalidateFavorites = useCallback(() => {
    // Keep favoritesReady true while refetching so optimistic Follow state is not
    // treated as "ended" for a tick (fetchedAt null → favoriteIds ignored).
    fetchFavorites();
  }, [fetchFavorites]);

  const removeFavoriteLocally = useCallback((startupId: string) => {
    pendingAddIdsRef.current.delete(startupId);
    setFavorites(prev => {
      const next = prev.filter(favorite => resolveStartupId(favorite) !== startupId);
      if (next.length === prev.length) return prev;
      setFavoritesCountState(next.length);
      return next;
    });
  }, []);

  const addFavoriteLocally = useCallback((startupId: string, profile?: Partial<StartupProfile>) => {
    if (!startupId) return;
    pendingAddIdsRef.current.add(startupId);
    setFavorites(prev => {
      if (prev.some(favorite => resolveStartupId(favorite) === startupId)) return prev;
      const stub = {
        ...profile,
        id: startupId,
        userId: startupId,
      } as StartupProfile;
      const next = [stub, ...prev];
      setFavoritesCountState(next.length);
      return next;
    });
  }, []);

  const isFavorited = useCallback(
    (startupId: string) => favorites.some(f => f.id === startupId),
    [favorites]
  );

  return (
    <FavoritesContext.Provider
      value={{
        favorites,
        favoritesCount,
        hasUnseenFavorites,
        favoritesLoading,
        favoritesReady,
        isFavorited,
        refreshFavorites,
        invalidateFavorites,
        removeFavoriteLocally,
        addFavoriteLocally,
        setFavoritesCount: setFavoritesCountState,
        markFavoritesSeen,
      }}
    >
      {children}
    </FavoritesContext.Provider>
  );
};

export const useFavoritesContext = () => {
  const ctx = useContext(FavoritesContext);
  if (!ctx) throw new Error('useFavoritesContext must be used within FavoritesCountProvider');
  return ctx;
};

// Backward-compatible hook for Header.tsx (count + badge management)
export const useFavoritesCount = () => {
  const { favoritesCount, hasUnseenFavorites, setFavoritesCount } = useFavoritesContext();
  return { favoritesCount, hasUnseenFavorites, setFavoritesCount };
};
